import { z } from 'zod';
import { count, isoDate, isoTimestamp, ratio } from './primitives.js';
/*
 * GET /quiz — quiz session analytics (quiz_events): the treningskalkulator
 * funnel with per-step drop-off plus a session list (journey trail).
 *
 * Recorded for EVERY visitor (no fbclid gate — unlike visitor_attribution).
 * Privacy by construction: rows carry only step numbers + question ids, never
 * answers — those live exclusively on the Momence lead the visitor consented
 * to on the contact step.
 */
const ok = z.literal(true);
/** The exact ad behind the session (parsed from the tagged landing URL). */
export const quizAdSchema = z.object({
    adName: z.string().nullable(), // utm_content = {{ad.name}}
    adId: z.string().nullable(),
    campaign: z.string().nullable(),
});
/** One beacon in a session's journey (chronological). */
export const quizJourneyEventSchema = z.object({
    at: isoTimestamp,
    event: z.string(), // 'land' | 'step' | 'cta_click' | 'lead_sent' | 'restart'
    step: z.number().nullable(),
    qid: z.string().nullable(),
});
/** One quiz session, aggregated from its beacons. */
export const quizSessionSchema = z.object({
    sessionId: z.string(),
    startedAt: isoTimestamp,
    lastSeenAt: isoTimestamp,
    durationSec: count,
    /** Farthest step reached (0=intro … contact, computing, result). */
    maxStep: count,
    maxQid: z.string().nullable(),
    device: z.enum(['mobile', 'desktop', 'unknown']),
    hasFbclid: z.boolean(),
    ad: quizAdSchema,
    ctaClicked: z.boolean(),
    leadSent: z.boolean(),
    /** They pressed «Ja, ring meg» on the result page (triggers a Slack notify). */
    callbackRequested: z.boolean(),
    /**
     * The actual person this session became — joined from lead_submissions via
     * the quizSessionId the lead POST carries. Post-consent only; null for
     * sessions that never submitted. `forwarded` = confirmed in Momence's lead
     * table (false = stored with us but Momence rejected; retry manually).
     */
    lead: z
        .object({
        name: z.string(),
        phone: z.string().nullable(),
        discovery: z.string().nullable(),
        momenceLeadId: z.string().nullable(),
        forwarded: z.boolean(),
    })
        .nullable(),
    restarts: count,
    journey: z.array(quizJourneyEventSchema),
});
/** One funnel stage: sessions that reached step N (maxStep >= N). */
export const quizStepSchema = z.object({
    step: count,
    /** Dominant question id observed at this step ('intro', 'mirror', …, 'result'). */
    qid: z.string().nullable(),
    reached: count,
    pctOfLanded: ratio,
    /** Sessions lost between the previous stage and this one. */
    dropFromPrev: count,
});
/**
 * Anonymous day-level answer tally (quiz_answer_daily — never linkable to a
 * session or person). Quitter distribution = all − completed.
 */
export const quizAnswerAggSchema = z.object({
    qid: z.string(),
    answerId: z.string(),
    /** Sessions that advanced past this question having picked this answer. */
    all: count,
    /** Final answers of sessions whose /api/lead succeeded. */
    completed: count,
});
export const quizResponse = z.object({
    ok,
    from: isoDate,
    to: isoDate,
    answers: z.array(quizAnswerAggSchema),
    totals: z.object({
        sessions: count,
        started: count, // maxStep >= 1
        reachedContact: count, // saw the contact step (qid 'contact')
        reachedResult: count, // saw the result screen (qid 'result')
        ctaClicked: count,
        leadsSent: count,
        /** reachedResult ÷ started. */
        completionPct: ratio,
        /** leadsSent ÷ started. */
        leadPct: ratio,
    }),
    steps: z.array(quizStepSchema),
    /** Newest sessions first, capped server-side. */
    sessions: z.array(quizSessionSchema),
});
//# sourceMappingURL=quiz.js.map